/**
 * @fileoverview Scout Eval Module
 * @description Routes the Scout evaluation workspace: imported runs, one run scorecard,
 * and run-over-run comparison.
 * @module features/ScoutEval/pages/ScoutEval
 */

import React, { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { Route, Routes } from 'react-router-dom';
import { RunsPage } from './RunsPage';
import { RunPage } from './RunPage';
import { ComparePage } from './ComparePage';
import { addRun, deleteRun, loadRuns } from '../utils/scoutStore';
import { pruneRunDetails, saveRunDetails } from '../utils/scoutDetailStore';
import { parseScoutReport, splitRunContent } from '../utils/scoutReportParser';
import './ScoutEval.css';

/**
 * Render the Scout evaluation workspace.
 * @returns {JSX.Element} Module routes.
 */
export function ScoutEval() {
  const [runs, setRuns] = useState(() => loadRuns());
  const [isImporting, setIsImporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastImportedId, setLastImportedId] = useState('');

  const importReport = useCallback(async (file) => {
    setIsImporting(true);
    setErrorMessage('');
    try {
      const { record, details } = splitRunContent(parseScoutReport(await file.text(), file.name));
      const stored = addRun(record);
      await saveRunDetails(record.id, details);
      await pruneRunDetails(stored.map((run) => run.id));
      setRuns(stored);
      setLastImportedId(record.id);
      return record;
    } catch (parseError) {
      setErrorMessage(parseError.message || 'This report could not be read.');
      return null;
    } finally {
      setIsImporting(false);
    }
  }, []);

  const removeRun = useCallback(async (runId) => {
    const stored = deleteRun(runId);
    setRuns(stored);
    await pruneRunDetails(stored.map((run) => run.id));
  }, []);

  const pageProps = {
    runs,
    isImporting,
    errorMessage,
    lastImportedId,
    onImport: importReport,
    onDelete: removeRun,
    onDismissError: () => setErrorMessage(''),
  };

  return (
    <Box className="scout-page">
      <Routes>
        <Route index element={<RunsPage {...pageProps} />} />
        <Route path="compare" element={<ComparePage {...pageProps} />} />
        <Route path=":runId" element={<RunPage {...pageProps} />} />
      </Routes>
    </Box>
  );
}

export default ScoutEval;
